import React from 'react';
import RecruiterCandidateCardFooter from './RecruiterCandidateCardFooter';
import RecruiterCandidateCardHeader from './RecruiterCandidateCardHeader';
import { ApplicationStatus, UIUserRecommendation, UserExperienceLevelType, UserPlacementTags } from 'types';
import { generateExpectedSalaryString } from 'src/utils/profileAdditionalInfo';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { HiClock } from 'react-icons/hi';
import { useUserContext } from 'src/context/UserProvider';
import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import { Badge } from 'flowbite-react';
import moment from 'moment';
interface Props {
	showBulkSelectUi?: boolean;
	isSelected?: boolean;
	isCardSelected: boolean;
	data: UIUserRecommendation;
	handleViewProfile: (arg: string) => void;
	handler: string;
	showShortListUi?: boolean;
	isShortListed?: boolean;
	disableStartProcess?: boolean;
	disableViewProfile?: boolean;
	isPositionExpired?: boolean;
	showBlur?: boolean;
	placedElseWhere?: boolean;
	disableShortListApi?: boolean;
	onShortlist?: () => void;
	isRejectedFromApplicants?: boolean; // True if rejected user was previously shortlisted (from Applicants tab)
}

const RecruiterCandidateCard = ({
	showBulkSelectUi = false,
	isSelected = false,
	isCardSelected,
	data,
	handleViewProfile,
	handler,
	showShortListUi,
	isShortListed,
	disableStartProcess = false,
	disableViewProfile = false,
	isPositionExpired = false,
	showBlur,
	placedElseWhere,
	disableShortListApi = false,
	onShortlist,
	isRejectedFromApplicants = false,
}: Props) => {
	const { isAccountManagerLoggedIn } = useUserContext();
	const hasShownInterest =
		data?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.APPLIED_BY_JOBSEEKER) || false;
	const hasShownDisInterest =
		data?.application?.history?.map(h => h.newStatus).includes(ApplicationStatus.JOBSEEKER_OPTED_OUT) || false;

	const placementStatus =
		data?.user?.placementStats?.status === UserPlacementTags.Placed && !data?.user?.flags?.isPlaced
			? 'Available'
			: data?.user?.placementStats?.status || 'Available';

	return (
		<>
			<div
				data-div-id={handler}
				className={classNames(
					`rounded-[12px] bg-white dark:bg-[#1F2A37] border border-solid md:hover:shadow-up transition-shadow ${isCardSelected
						? 'border-[#7777E7] universal-card-box-shadow !bg-[#E5F0FF] dark:!bg-[#7777E757]'
						: 'dark:border-[#c7c9d91a] border-[#EBEBF0] shadow-[0px_1px_3px_0px_rgba(167,_167,_167,_0.09),_0px_1px_2px_0px_rgba(226,_226,_226,_0.00)]'
					} relative flex flex-col h-full hover:cursor-pointer `,
					isSelected ? '!border-[#7777E7]' : '',
					showBlur ? 'blur-[7.5px] pointer-events-none' : '',
					showBulkSelectUi && data?.application?.status === ApplicationStatus.REJECTED ? 'opacity-60 cursor-not-allowed' : '',
				)}
			>
				{isAccountManagerLoggedIn && data?.isProfileShared ? (
					<Tooltip title={'title'} placement="bottom">
						<div className="absolute top-[-11px] right-[10px]">
							<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">Profile Shared</Badge>
						</div>
					</Tooltip>
				) : null}
				{/* data.isRecommended ? (
						<Tooltip title={'title'} placement="bottom">
							<div className="absolute top-[-11px] right-[10px]">
								<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">Recommended</Badge>
							</div>
						</Tooltip> */}
				{/* Rejected user origin badge - show if user is rejected */}
				{(data?.application?.status === ApplicationStatus.REJECTED ||
					data?.application?.status === ApplicationStatus.AUTO_REJECTED) &&
					isRejectedFromApplicants ? (
					<Tooltip title="Rejected from Applicants (was shortlisted)" placement="bottom">
						<div className="absolute top-[-11px] right-[10px]">
							<Badge className="text-[#99154B] bg-[#FFE5F0] rounded-[6px]">Applicants</Badge>
						</div>
					</Tooltip>
				) : (data?.application?.status === ApplicationStatus.REJECTED ||
					data?.application?.status === ApplicationStatus.AUTO_REJECTED) &&
					!isRejectedFromApplicants ? (
					<Tooltip title="Rejected from Recommendations (applied but not shortlisted)" placement="bottom">
						<div className="absolute top-[-11px] right-[10px]">
							<Badge className="text-[#99154B] bg-[#FFE5F0] rounded-[6px]">Recommendations</Badge>
						</div>
					</Tooltip>
				) : null}
				{/* Recommendation badge (uses server-computed badge text) - only show if not rejected */}
				{(() => {
					if (
						data?.application?.status === ApplicationStatus.REJECTED ||
						data?.application?.status === ApplicationStatus.AUTO_REJECTED
					) {
						return null; // Don't show recommendation badge for rejected users
					}
					const badgeText = isAccountManagerLoggedIn
						? data?.badgeTextForAccountManager
						: data?.badgeTextForJobseeker;
					if (!badgeText) return null;
					return (
						<Tooltip title={badgeText} placement="bottom">
							<div className="absolute top-[-11px] right-[10px]">
								<Badge className="text-[#42389D] bg-[#E5EDFF] rounded-[6px]">{badgeText}</Badge>
							</div>
						</Tooltip>
					);
				})()}
				{showBulkSelectUi ? (
					<FaRegCircleCheck
						className={`w-[24px] h-[24px] absolute right-[-7px] pointer-events-none ${isSelected ? 'bg-[#7777E7] text-[#CCDDFF]' : 'bg-white text-[#555770]'
							} rounded-full top-[-7px] `}
					/>
				) : null}
				<div className="p-4 grow-2 overflow-hidden" onClick={() => handleViewProfile(handler)}>
					<div className="flex items-start justify-between gap-2">
						<div className="flex-1">
							<RecruiterCandidateCardHeader
								isVerified={data.user.flags.isTrusted ?? false}
								imgUrl={
									(data.user?.profile?.profileImageUrl && typeof data.user.profile.profileImageUrl === 'string'
										? data.user.profile.profileImageUrl
										: data.user?.profile?.imageUrl && typeof data.user.profile.imageUrl === 'string'
											? data.user.profile.imageUrl
											: '') || ''
								}
								name={data.user.name}
								company={data.user.profile.currentCompany ?? ''}
								role={data.user.profile.currentRole ?? ''}
								yearsOfExperience={data.user?.userPreference?.experienceLevel ?? UserExperienceLevelType.FRESHER}
								matchPercentage={Math.round(data.percentageMatch ?? 0)}
								primaryTechStack={data.user.profile.primaryTechStack}
							/>
						</div>
						{isAccountManagerLoggedIn && data.expiryDate ? (
							<div className="flex flex-col items-end gap-[4px] flex-shrink-0">
								<p className="text-gray-500 dark:text-[#C7C9D9] font-inter text-[12px] font-[400] leading-[16px]">
									Expires at
								</p>
								<div className="flex items-center gap-[4px]">
									<HiClock className="text-[#E57A00] dark:text-[#FDED72] h-[16px] w-[16px]" />
									<p className="text-[#E57A00] dark:text-[#FDED72] font-inter text-[12px] font-[400] leading-[16px] whitespace-nowrap">
										{moment(data.expiryDate).format('DD MMM, hh:mm A')}
									</p>
								</div>
							</div>
						) : null}
					</div>
					<div className="grid grid-cols-3 gap-y-2 mt-2">
						{isAccountManagerLoggedIn && data.user.subRole ? (
							<div className="flex gap-1 flex-col mt-[10px]">
								<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">Student Type</div>
								<div className="font-inter text-sm font-[500]">{data.user.subRole}</div>
							</div>
						) : null}
						<div className="flex gap-1 flex-col mt-[10px]">
							<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">Placement Status</div>
							<div className="font-inter text-sm font-[500]">{placementStatus}</div>
						</div>
						{data.user.profile.expectedJoining ? (
							<div className="flex gap-1 flex-col mt-[10px]">
								<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">
									Available to join
								</div>
								<div className="font-inter text-sm font-[500]">
									{data.user.profile.expectedJoining?.replaceAll('_', ' ') ?? '-'}
								</div>
							</div>
						) : null}

						{/* commented for now */}
						{/* <div className="flex gap-4 flex-col mt-2">
							<div className="text-[12px] text-[#CDF] min-w-max">Skills assessed</div>
							<SkillsAssessedWrapper skills={assessedSkills} />
						</div> */}

						{/* Predicted salary starting for Masai students should always be 3.5 as lower bound && if there lower bound is <5 then it should be 3.5 else it’s 5 */}
						{/* {data.expectedSalary?.min || data.expectedSalary?.max ? (
							<div className="flex gap-1 flex-col mt-2">
								<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">Predicted salary</div>
								<div className="font-inter text-sm font-[500]">
									{data.expectedSalary?.min || data.expectedSalary?.max
										? generateExpectedSalaryString(
												data.expectedSalary?.min,
												data.expectedSalary?.min === data.expectedSalary?.max ? 0 : data.expectedSalary?.max,
										  )
										: '-'}
								</div>
							</div>
						) : null} */}

						{data.user.phone ? (
							<div className="flex gap-1 flex-col mt-2">
								<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">Contact number</div>
								<div className="font-inter text-sm font-[500]">{data.user.phone || '-'}</div>
							</div>
						) : null}
						{'lmsTechStack' in (data.user || {}) ? (
							<div className="flex gap-1 flex-col mt-[10px]">
								<div className="text-[12px] text-[#555770] dark:text-[#CDF] min-w-max font-inter">LMS Tech stack</div>
								<div className="font-inter text-sm font-[500]">
									{data.user.lmsTechStackNames?.length ? data.user.lmsTechStackNames.join(', ') : '-'}
								</div>
							</div>
						) : null}
					</div>
				</div>
				<RecruiterCandidateCardFooter
					userId={data.user?._id}
					data={data}
					isShortListed={isShortListed}
					isPositionExpired={isPositionExpired}
					showShortListUi={showShortListUi}
					level={Math.round(data.user.evaluation.level ?? 0)}
					disableStartProcess={disableStartProcess}
					disableViewProfile={disableViewProfile}
					hasShownDisInterest={hasShownDisInterest}
					hasShownInterest={hasShownInterest}
					handleViewProfile={handleViewProfile}
					handler={handler}
					placedElseWhere={placedElseWhere}
					disableShortListApi={disableShortListApi}
					onShortlist={onShortlist}
					showBulkSelectUi={showBulkSelectUi}
				/>
			</div>
		</>
	);
};

export default RecruiterCandidateCard;
